import { ReactNode, useContext } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { useTranslation } from 'libs/i18n'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { ContextMenu, Currency, PayoutDestination } from 'shared/components'
import type { Option } from 'shared/components/ContextMenu/types'
import type { CardTableColumn } from 'styles/table'
import { PayoutStatus } from 'features/payouts/molecules/PayoutStatus/PayoutStatus'
import { formatPayoutDate } from 'features/payouts/utils/payout'
import type { PayoutItem } from 'features/payouts/types/payouts.types'

type UseColumnsProps = {
	menuOptions: Option[]
	onOpen: (payout: PayoutItem) => void
}

const cell = (render: (payout: PayoutItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as PayoutItem)

const muted = 'block whitespace-nowrap text-[12px] tabular-nums text-muted-foreground'
const numeric = `${muted} text-right`
const dash = <span className="text-muted-foreground">—</span>

export const useColumns = ({ menuOptions, onOpen }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const settingsCtx = useContext(StoreSettingsContext)
	const isIceland = settingsCtx?.storeSettings?.country === 'IS'

	return [
		{
			label: t('Payout'),
			width: '80px',
			renderCell: cell((item) => (
				<button
					type="button"
					className="rounded-md bg-muted px-2 py-1 text-[12px] font-semibold tabular-nums text-primary transition-colors hover:bg-primary-soft"
					onClick={() => onOpen(item)}>
					#{item.reference}
				</button>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Recipient'),
			width: 'minmax(170px, 1.2fr)',
			renderCell: cell((item) => (
				<span className="block min-w-0 leading-tight">
					<span className="block truncate text-[13px] font-medium text-foreground">
						{item.account?.name || '—'}
					</span>
					<span className="block truncate text-[11px] text-muted-foreground">
						{item.account?.email}
					</span>
				</span>
			))
		},
		{
			label: t('Created'),
			width: '160px',
			renderCell: cell((item) => <span className={muted}>{formatPayoutDate(item.created)}</span>)
		},
		{
			label: t('Payout method'),
			width: '180px',
			renderCell: cell((item) => <PayoutDestination destination={item.payout_method} />)
		},
		...(isIceland
			? [
					{
						label: t('SSN'),
						width: '110px',
						renderCell: cell((item) =>
							item.account?.ssn ? <span className={muted}>{item.account.ssn}</span> : dash
						)
					}
				]
			: []),
		{
			label: t('Total'),
			width: '100px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.total} />)
		},
		{
			label: t('Commission total'),
			width: '140px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.commission} />)
		},
		{
			label: t('Cost'),
			width: '90px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency className={numeric} price={item.takedown_and_storage_fees} />
			))
		},
		{
			label: t('Amount'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency
					className="block whitespace-nowrap text-right font-display text-[13px] font-bold tabular-nums text-foreground"
					price={item.amount}
				/>
			))
		},
		{
			label: t('Status'),
			width: '120px',
			renderCell: cell((item) => <PayoutStatus status={item.status} />)
		},
		{
			label: t('Transaction Reference'),
			width: 'minmax(170px, 1fr)',
			renderCell: cell((item) =>
				item.tx_reference ? (
					<span className="block truncate text-[12px] font-medium text-muted-foreground">
						{item.tx_reference}
					</span>
				) : (
					dash
				)
			)
		},
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={menuOptions}
					label={t('Actions for payout {{ id }}', { id: item.reference })}
				/>
			))
		}
	]
}
